import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'auto';
export type ResolvedTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'marketplace_theme_mode';
const DARK_CLASSNAME = 'dark';

const SYSTEM_DARK_THEME_QUERY = '(prefers-color-scheme: dark)';

function isThemeMode(value: unknown): value is ThemeMode {
    return value === 'light' || value === 'dark' || value === 'auto';
}

function readStoredTheme(): ThemeMode | null {
    try {
        const rawTheme = localStorage.getItem(THEME_STORAGE_KEY);
        return isThemeMode(rawTheme) ? rawTheme : null;
    } catch {
        return null;
    }
}

function resolveAutoTheme(): ResolvedTheme {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'dark';
    }

    return window.matchMedia(SYSTEM_DARK_THEME_QUERY).matches ? 'dark' : 'light';
}

function resolveEffectiveTheme(themeMode: ThemeMode): ResolvedTheme {
    if (themeMode === 'auto') return resolveAutoTheme();
    return themeMode;
}

function persistTheme(themeMode: ThemeMode): void {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    } catch {
        // Ignorar storage bloqueado/restringido.
    }
}

const THEME_BACKGROUND_COLORS: Record<ResolvedTheme, string> = {
    dark: '#0A0D14',
    light: '#F0F4F8',
};

function syncAndroidWindowBackground(resolved: ResolvedTheme): void {
    const bridge = (window as unknown as Record<string, unknown>).AndroidWindowBackground as
        | { setColor?: (hex: string) => void }
        | undefined;

    bridge?.setColor?.(THEME_BACKGROUND_COLORS[resolved]);
}

function applyThemeToDocument(resolved: ResolvedTheme): void {
    if (typeof document === 'undefined') {
        return;
    }

    const root = document.documentElement;
    root.setAttribute('data-theme', resolved);
    root.classList.toggle(DARK_CLASSNAME, resolved === 'dark');
    root.style.setProperty('color-scheme', resolved);
    syncAndroidWindowBackground(resolved);
}

let autoThemeMediaQuery: MediaQueryList | null = null;
let autoThemeChangeListener: (() => void) | null = null;

function clearAutoThemeListener(): void {
    if (autoThemeMediaQuery && autoThemeChangeListener) {
        autoThemeMediaQuery.removeEventListener('change', autoThemeChangeListener);
    }

    autoThemeMediaQuery = null;
    autoThemeChangeListener = null;
}

function startAutoThemeListener(onChange: () => void): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return;
    }

    autoThemeMediaQuery = window.matchMedia(SYSTEM_DARK_THEME_QUERY);
    autoThemeChangeListener = onChange;
    autoThemeMediaQuery.addEventListener('change', autoThemeChangeListener);
}

interface ThemeState {
    themeMode: ThemeMode;
    resolvedTheme: ResolvedTheme;
    isThemeInitialized: boolean;
    initializeTheme: () => void;
    setThemeMode: (themeMode: ThemeMode) => void;
    tickAutoTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
    themeMode: 'dark',
    resolvedTheme: 'dark',
    isThemeInitialized: false,
    initializeTheme: () => {
        const themeMode = readStoredTheme() ?? 'dark';
        const resolved = resolveEffectiveTheme(themeMode);
        applyThemeToDocument(resolved);
        set({ themeMode, resolvedTheme: resolved, isThemeInitialized: true });
        clearAutoThemeListener();

        if (themeMode === 'auto') {
            startAutoThemeListener(() => get().tickAutoTheme());
        }
    },
    setThemeMode: (themeMode) => {
        clearAutoThemeListener();
        const resolved = resolveEffectiveTheme(themeMode);
        persistTheme(themeMode);
        applyThemeToDocument(resolved);
        set({ themeMode, resolvedTheme: resolved });

        if (themeMode === 'auto') {
            startAutoThemeListener(() => get().tickAutoTheme());
        }
    },
    tickAutoTheme: () => {
        const { themeMode } = get();
        if (themeMode !== 'auto') {
            clearAutoThemeListener();
            return;
        }
        const resolved = resolveAutoTheme();
        applyThemeToDocument(resolved);
        set({ resolvedTheme: resolved });
    },
}));
